import styled, { css } from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { PlainTooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import I18n from "pro-features/components/WithTranslation/src/I18n";
import { allTrackers } from "app/services/track/track";
import { ensureProtocol } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";

const LinkOutIcon = styled(SWReactIcons)<{ $alwaysShowActions: boolean; $hasMultipleLp: boolean }>`
    display: ${({ $alwaysShowActions }) => ($alwaysShowActions ? "block" : "none")};
    min-width: 16px;
    min-height: 16px;
`;

const HrefContainer = styled.a<{ $withMaxWidth?: boolean; $inTooltip?: boolean }>`
    display: flex;
    column-gap: 4px;
    max-width: ${({ $withMaxWidth }) => ($withMaxWidth ? "100%" : "calc(100% - 24px)")};
    align-items: center;

    ${({ $inTooltip }) =>
        $inTooltip &&
        css`
            margin-top: 4px;
        `};

    &:hover {
        ${SWReactIcons} {
            display: block;
        }
    }
`;

const Href = styled.div<{ $isLight?: boolean; $withMaxWidth?: boolean }>`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    ${({ $isLight }) =>
        $isLight
            ? setFontVariant("tooltip", colorsPalettes.blue[300])
            : setFontVariant("input2", colorsPalettes.blue[400])};
`;

const AllLandingPagesContainer = styled.div`
    ${setFontVariant("input2", colorsPalettes.carbon[500])};
    margin-left: 8px;
`;

const TooltipTitle = styled(I18n)`
    ${setFontVariant("listItem4", colorsPalettes.bluegrey[100])};
    white-space: nowrap;
`;

interface LandingPageProps {
    page: string;
    alwaysShowActions?: boolean;
    inTooltip?: boolean;
    hasMultipleLp?: boolean;
}

const LandingPage = ({ page, alwaysShowActions = false, inTooltip = false, hasMultipleLp }: LandingPageProps) => {
    const handleLandingPageLinkClick = () => {
        allTrackers.dispatchTrackEvent("External Link", "click", "Ads/URL/Pop Up");
    };

    return (
        <HrefContainer
            href={ensureProtocol(page)}
            onClick={handleLandingPageLinkClick}
            target="_blank"
            rel="noreferrer"
            $withMaxWidth={!hasMultipleLp || inTooltip}
            $inTooltip={inTooltip}
        >
            <Href
                $isLight={inTooltip}
                $withMaxWidth={!hasMultipleLp}
            >
                {page}
            </Href>
            <LinkOutIcon
                $alwaysShowActions={alwaysShowActions}
                iconName="link-out"
                size="xs"
                iconColor={colorsPalettes.blue[inTooltip ? 300 : 400]}
                $hasMultipleLp={hasMultipleLp}
            />
        </HrefContainer>
    );
};

interface UrlAnchorProps {
    landingPagesLinks: string[];
}

export const UrlAnchor = ({ landingPagesLinks = [] }: UrlAnchorProps) => {
    if (!landingPagesLinks.length) return null;

    const hasMultipleLandingPages = landingPagesLinks.length > 1;
    const landingPagesLinksForTooltip = hasMultipleLandingPages ? landingPagesLinks.slice(1) : landingPagesLinks;

    return (
        <Flex>
            <LandingPage
                page={landingPagesLinks[0]}
                alwaysShowActions={hasMultipleLandingPages}
                hasMultipleLp={hasMultipleLandingPages}
            />
            {hasMultipleLandingPages ? (
                <PlainTooltip
                    allowHover
                    closeDelay={100}
                    maxWidth={312}
                    tooltipContent={
                        <>
                            {landingPagesLinksForTooltip.length > 1 ? (
                                <TooltipTitle>ad.intelligence.advertiser.ad.modal.landing.pages.tooltip</TooltipTitle>
                            ) : null}
                            {landingPagesLinksForTooltip.map((link: string, i: number) => (
                                <LandingPage
                                    alwaysShowActions
                                    inTooltip
                                    key={i}
                                    page={link}
                                    hasMultipleLp={hasMultipleLandingPages}
                                />
                            ))}
                        </>
                    }
                >
                    <AllLandingPagesContainer>+{landingPagesLinks.length - 1}</AllLandingPagesContainer>
                </PlainTooltip>
            ) : null}
        </Flex>
    );
};
